import React, { useEffect } from 'react';

import { FilterChipList, FilterGroup, FilterStack } from '@pkg/components/filters';
import { type Collection } from '@pkg/graphql/codegen/graphql';
import { useAppSelector, useGetCollections } from '@pkg/hooks';
import { useMediaQuery } from '@pkg/providers';

import { useNFTsFiltersStore } from '@app/store/zustand';
import { availableFilterSetMarketPlace } from '@app/store/zustand/profile-page/useNFTsFiltersStore';

import { CollectionList } from '../CollectionList/CollectionList.tsx';

import { GeneralAction } from './GeneralAction.tsx';

export function NftFiltersSecondary() {
    const { tablet } = useMediaQuery();
    const marketplaceV1Enabled = useAppSelector((state) => state.featureFlags.features.marketplaceV1Enabled);

    const availableFilters = useNFTsFiltersStore((state) => state.availableFilters);
    const setAvailableFilters = useNFTsFiltersStore((state) => state.setAvailableFilters);

    useEffect(() => {
        if (marketplaceV1Enabled) {
            setAvailableFilters(availableFilterSetMarketPlace);
        }
    }, [marketplaceV1Enabled, setAvailableFilters]);

    const parsedFilter = availableFilters.parse;
    const filterKeys = Object.keys(parsedFilter).filter((key) => key !== 'collections');

    // TODO: Is there an issue with the `idsOpt > collectionIds` param?
    // I do not return ANY collections in local development.
    const collectionIds = availableFilters.get('collections');
    const { data: collections, isLoading: isLoadingCollections } = useGetCollections({
        variables: {
            filter: {
                ids: collectionIds,
            },
        },
    });

    const filterMarkup = filterKeys
        .map((filterKey) => {
            const filterOptionKeys = parsedFilter[filterKey] as string[];

            const itemsMarkup = filterOptionKeys.map((option) => (
                <FilterChipList.Item key={`${filterKey}-${option}`}>
                    <GeneralAction filterKey={filterKey} filterOption={option} />
                </FilterChipList.Item>
            ));

            return itemsMarkup.length ? (
                <FilterStack.Item key={filterKey}>
                    <FilterGroup label={filterKey} count={filterOptionKeys.length} initialExpanded>
                        <FilterChipList>{itemsMarkup}</FilterChipList>
                    </FilterGroup>
                </FilterStack.Item>
            ) : null;
        })
        .filter((markup): markup is JSX.Element => Boolean(markup));

    const collectionFilterMarkup = collections?.length
        ? collections.map((collection: Collection) => (
              <CollectionList.Item key={collection.id}>
                  <CollectionList.Action collectionData={collection} />
              </CollectionList.Item>
          ))
        : null;

    return (
        <FilterStack>
            {filterMarkup}

            {!isLoadingCollections && collectionFilterMarkup?.length ? (
                <FilterStack.Item>
                    <FilterGroup
                        label="Collections"
                        count={collectionFilterMarkup.length}
                        scrollable={tablet}
                        initialExpanded
                    >
                        <CollectionList>{collectionFilterMarkup}</CollectionList>
                    </FilterGroup>
                </FilterStack.Item>
            ) : null}
        </FilterStack>
    );
}
